import React from "react";
import {
    Placeholder,
    PlaceholderLine,
    PlaceholderParagraph,
} from "semantic-ui-react";
import ReportCard from "./ReportCard";
import styles from "./reportDashboard.module.scss";

const columnClass = {
    figure: styles.figure,
    table: styles.table,
    graph: styles.graph,
};

const placeholderClass = {
    figure: styles.figurePlaceholder,
    table: styles.tablePlaceholder,
    graph: styles.graphPlaceholder,
};

function ReportPlaceholderCard({ report }) {
    const paragraphCount = report.type === "figure" ? 1 : 3;

    return (
        <div
            aria-busy='true'
            className={`${styles.card} ${
                columnClass[report.type] || styles.figure
            }`}
        >
            <div className={styles.header}>
                <div>
                    <h4>{report.name}</h4>
                    {report.usesDateFilter === false && (
                        <span className={styles.filterNote}>
                            Not affected by period filters
                        </span>
                    )}
                </div>
            </div>
            <div
                className={`${styles.placeholderBody} ${
                    placeholderClass[report.type] || styles.figurePlaceholder
                }`}
            >
                <Placeholder fluid>
                    {Array.from({ length: paragraphCount }, (_, index) => (
                        <PlaceholderParagraph key={index}>
                            <PlaceholderLine />
                            <PlaceholderLine />
                            <PlaceholderLine />
                        </PlaceholderParagraph>
                    ))}
                </Placeholder>
            </div>
        </div>
    );
}

function ReportErrorCard({ report, onRetry, retrying, retryFailed }) {
    return (
        <div
            className={`${styles.card} ${styles.errorCard} ${
                columnClass[report.type] || styles.figure
            }`}
        >
            <div className={styles.header}>
                <h4>{report.name}</h4>
            </div>
            <p>This report could not be loaded.</p>
            {retryFailed && <p>The retry failed. Please try again.</p>}
            <button
                className='btn btn-outline-primary btn-sm'
                disabled={retrying}
                onClick={() => onRetry(report.batchKey)}
                type='button'
            >
                {retrying ? "Retrying…" : "Retry"}
            </button>
        </div>
    );
}

function ReportSection({
    title,
    description,
    reports,
    onRetry,
    retrying,
    retryErrors,
}) {
    if (reports.length === 0) {
        return null;
    }

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <h3>{title}</h3>
                {description && <p>{description}</p>}
            </div>
            <div className={styles.grid}>
                {reports.map((report) =>
                    report.status === "loading" ? (
                        <ReportPlaceholderCard key={report.id} report={report} />
                    ) : report.status === "error" ? (
                        <ReportErrorCard
                            key={report.id}
                            report={report}
                            onRetry={onRetry}
                            retrying={retrying[report.batchKey] === true}
                            retryFailed={retryErrors[report.batchKey] === true}
                        />
                    ) : (
                        <ReportCard key={report.id} report={report} />
                    ),
                )}
            </div>
        </div>
    );
}

function ReportGrid({
    reports,
    onRetry,
    retrying,
    retryErrors,
}) {
    if (reports.length === 0) {
        return (
            <div className={styles.empty}>
                <h4>No reports configured</h4>
                <p>Reports will appear here as they are added.</p>
            </div>
        );
    }

    const dateFilteredReports = reports.filter(
        (report) => report.usesDateFilter !== false,
    );
    const unfilteredReports = reports.filter(
        (report) => report.usesDateFilter === false,
    );

    return (
        <>
            <ReportSection
                title='Period reports'
                description='These reports use the selected period or custom date range.'
                reports={dateFilteredReports}
                onRetry={onRetry}
                retrying={retrying}
                retryErrors={retryErrors}
            />
            <ReportSection
                title='All-time member context'
                description='These reports are not affected by the selected period or custom date range.'
                reports={unfilteredReports}
                onRetry={onRetry}
                retrying={retrying}
                retryErrors={retryErrors}
            />
        </>
    );
}

export default ReportGrid;
